export interface RetryOptions {
  baseDelay?: number
  maxDelay?: number
  maxAttempts?: number
}

/**
 * Retries a function with exponential backoff and random jitter;
 * avoids contention for shared resources by multiple clients.
 *
 * Mirrors rd/retry/retry.go::ExpBackoffWithJitter
 *
 * @param fn - Retried function
 * @param options - Retry configuration
 * @returns The resolved value from fn or throws the last encountered error
 */
export async function retryWithExponentialBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const { baseDelay = 500, maxDelay = 5000, maxAttempts = 3 } = options

  if (maxAttempts < 1) {
    throw new Error("maxAttempts must be at least 1")
  }

  let lastError: unknown

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn()
    } catch (err) {
      lastError = err

      if (attempt === maxAttempts) break

      let delay = baseDelay * Math.pow(2, attempt - 1)
      delay = Math.min(delay, maxDelay)

      const jitter = Math.random() * (delay / 2)
      const totalDelay = delay + jitter

      await new Promise((resolve) => setTimeout(resolve, totalDelay))
    }
  }

  throw new Error(
    `All ${maxAttempts} attempts failed. Last error: ${
      lastError instanceof Error ? lastError.message : String(lastError)
    }`
  )
}
